import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  createSinglesTeam,
  createPairTeam,
  transferCaptaincy,
  clearTeamError,
  clearTeamSuccess,
} from '../../store/slices/teamSlice';
import {
  enrollTeam,
  clearEnrollmentError,
  clearEnrollSuccess,
} from '../../store/slices/enrollmentSlice';
import { fetchLeague, fetchSeasons, fetchDivisionsForSeason } from '../../store/slices/leagueSlice';
import { TeamType, Gender } from '@sulis-liiga/common';
import { resolveEnrollmentSeason } from '../../utils/season';
import type { PartnerSearchResult } from '@sulis-liiga/common';
import PartnerSearchField from '../../components/team/PartnerSearchField';
import EnrollDivisionPicker, { eligibleLadders } from '../../components/team/EnrollDivisionPicker';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';

// ─── Step model ─────────────────────────────────────────────────────────────
// Singles skip the Partner and Captain steps; pairs include them. The Division
// step is always last and mandatory.

type StepKey = 'type' | 'partner' | 'captain' | 'name' | 'divisions';

const STEP_LABELS: Record<StepKey, string> = {
  type: 'Team type',
  partner: 'Partner',
  captain: 'Captain',
  name: 'Name',
  divisions: 'Divisions',
};

function StepIndicator({ steps, currentIndex }: { steps: StepKey[]; currentIndex: number }) {
  return (
    <ol className="flex items-center gap-1 mb-6" aria-label="Progress">
      {steps.map((key, i) => (
        <li key={key} className="flex items-center gap-1">
          <span
            className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium ${
              i < currentIndex
                ? 'bg-primary-600 text-white'
                : i === currentIndex
                ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-600'
                : 'bg-gray-100 text-gray-400'
            }`}
            aria-current={i === currentIndex ? 'step' : undefined}
            title={STEP_LABELS[key]}
          >
            {i < currentIndex ? '✓' : i + 1}
          </span>
          {i < steps.length - 1 && <span className="text-gray-300 text-xs">–</span>}
        </li>
      ))}
    </ol>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const CreateTeamPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);
  const { loading: teamLoading, error: teamError } = useSelector((state: RootState) => state.team);
  const { error: enrollError } = useSelector((state: RootState) => state.enrollment);
  const { selectedLeagueId, bannerLeagues } = useSelector((state: RootState) => state.ui);
  const {
    seasons,
    divisions,
    loading: leagueLoading,
  } = useSelector((state: RootState) => state.league);

  // Enroll into the active season, or the earliest upcoming one when the admin has
  // set up next season's (still-UPCOMING) divisions but not yet activated it.
  const enrollSeason = useMemo(() => resolveEnrollmentSeason(seasons), [seasons]);

  // Wizard state
  const [teamType, setTeamType] = useState<TeamType>(TeamType.SINGLES);
  const [partner, setPartner] = useState<PartnerSearchResult | null>(null);
  const [captainChoice, setCaptainChoice] = useState<'me' | 'partner'>('me');
  const [teamName, setTeamName] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [partnerError, setPartnerError] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [failedLadders, setFailedLadders] = useState<string[]>([]);
  const [createdTeamId, setCreatedTeamId] = useState<string | null>(null);

  const isPair = teamType !== TeamType.SINGLES;
  const steps: StepKey[] = isPair
    ? ['type', 'partner', 'captain', 'name', 'divisions']
    : ['type', 'name', 'divisions'];
  const currentStep = steps[stepIndex];

  // Resolve the active (banner-selected) league's tenant, then load its divisions.
  const { resolved: tenantResolved } = useActiveLeagueTenant(undefined);
  const activeLeague = bannerLeagues.find(l => l.leagueId === selectedLeagueId) ?? bannerLeagues[0];
  const leagueId = activeLeague?.leagueId ?? selectedLeagueId ?? undefined;

  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchSeasons(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  useEffect(() => {
    if (leagueId && enrollSeason?.id && tenantResolved) {
      dispatch(fetchDivisionsForSeason({ leagueId, seasonId: enrollSeason.id }));
    }
  }, [dispatch, leagueId, tenantResolved, enrollSeason?.id]);

  // Reset transient slice flags on mount/unmount.
  useEffect(() => {
    dispatch(clearTeamError());
    dispatch(clearEnrollmentError());
    return () => {
      dispatch(clearTeamSuccess());
      dispatch(clearEnrollSuccess());
    };
  }, [dispatch]);

  // Keep the step index valid when switching between singles/pairs.
  useEffect(() => {
    setStepIndex(i => Math.min(i, steps.length - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPair]);

  // Clear an incompatible partner if the user switches back to singles.
  useEffect(() => {
    if (!isPair && partner) setPartner(null);
  }, [isPair, partner]);

  // The prospective team's player genders — one per player. Division eligibility
  // is size + gender rule, so these decide which ladders are offered (a singles
  // player sees singles ladders; a male+female pair sees mixed and no-restriction
  // doubles ladders). Falls back to MALE if a gender is missing.
  const genders = useMemo<Gender[]>(() => {
    const myGender = user?.gender ?? Gender.MALE;
    if (teamType === TeamType.SINGLES) return [myGender];
    return partner ? [myGender, partner.gender] : [myGender];
  }, [teamType, partner, user?.gender]);

  // Lookup of eligible ladders (for labelling failures).
  const ladderLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of eligibleLadders(divisions, genders)) map.set(l.groupId, l.baseName);
    return map;
  }, [divisions, genders]);

  // Drop any selected ladder that is no longer eligible (e.g. type/partner changed).
  useEffect(() => {
    setSelectedGroupIds(ids => ids.filter(id => ladderLabels.has(id)));
  }, [ladderLabels]);

  const seasonHasNoDivisions = !leagueLoading && divisions.length === 0;
  const error = teamError || enrollError;

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goNext = () => {
    if (currentStep === 'partner' && !partner) {
      setPartnerError('Please select a partner before continuing.');
      return;
    }
    setPartnerError('');
    setStepIndex(i => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setStepIndex(i => Math.max(i - 1, 0));

  // ── Submit ────────────────────────────────────────────────────────────────

  const canSubmit = selectedGroupIds.length > 0 && (!isPair || !!partner) && !submitting;

  const handleCreate = async () => {
    if (!canSubmit || !leagueId) return;
    dispatch(clearTeamError());
    dispatch(clearEnrollmentError());
    setSubmitting(true);
    setFailedLadders([]);

    try {
      // 1. Create the team. (For a pair, `canSubmit` guarantees a partner; the
      //  ternary narrows `partner` so no non-null assertion is needed.)
      const team =
        isPair && partner
          ? await dispatch(
              createPairTeam({
                type: teamType,
                partnerUserId: partner.id,
                name: teamName.trim() || undefined,
              }),
            ).unwrap()
          : await dispatch(createSinglesTeam({ name: teamName.trim() || undefined })).unwrap();

      // 2. Enroll into each selected ladder while the creator is still captain.
      // seasonId is intentionally omitted — the backend targets the upcoming
      // season's roster, or queues the team (season-less) when none exists yet.
      const results = await Promise.allSettled(
        selectedGroupIds.map(groupId =>
          dispatch(
            enrollTeam({
              teamId: team.id,
              req: { leagueId, divisionGroupId: groupId },
            }),
          ).unwrap(),
        ),
      );
      const failed = selectedGroupIds.filter((_, i) => results[i].status === 'rejected');

      // 3. Transfer captaincy last (enroll requires the creator to still be captain).
      if (isPair && captainChoice === 'partner' && partner) {
        try {
          await dispatch(
            transferCaptaincy({ teamId: team.id, newCaptainUserId: partner.id }),
          ).unwrap();
        } catch {
          // Non-fatal: team exists; captaincy can be transferred from the team page.
        }
      }

      if (failed.length > 0) {
        // Partial failure — keep the team, surface the failed ladders for retry.
        setCreatedTeamId(team.id);
        setFailedLadders(failed.map(id => ladderLabels.get(id) ?? id));
      } else {
        navigate(`/teams/${team.id}`);
      }
    } catch {
      // Team creation itself failed — error surfaced via slice state.
    } finally {
      setSubmitting(false);
    }
  };

  const userInitials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';
  const partnerInitials = partner
    ? `${partner.firstName?.[0] ?? ''}${partner.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';

  // ── Partial-failure result panel ─────────────────────────────────────────────

  if (createdTeamId && failedLadders.length > 0) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mt-8 bg-white rounded-lg shadow-sm p-8" role="status" aria-live="polite">
          <h2 className="text-2xl font-bold text-gray-900">
            Team created — some enrollments failed
          </h2>
          <p className="mt-2 text-gray-600">
            Your team was created, but we couldn't enroll it into:{' '}
            <strong>{failedLadders.join(', ')}</strong>. You can retry from your team page.
          </p>
          <div className="mt-6">
            <Link
              to={`/teams/${createdTeamId}`}
              className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              Go to my team
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Wizard ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/teams" className="text-sm text-primary-600 hover:underline">
        &larr; Back to Teams
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Create a team</h1>
      <p className="mt-1 text-gray-600">
        Form your team and enroll it into a division — this is how you enter{' '}
        {activeLeague?.name ?? 'the league'}.
      </p>

      <StepIndicator steps={steps} currentIndex={stepIndex} />

      {/* ── Step: Team type ─────────────────────────────────────────────────── */}
      {currentStep === 'type' && (
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">{STEP_LABELS.type}</h2>
          <p className="mt-1 text-xs text-gray-400">
            Doubles covers men's, women's, and mixed — which divisions you can enter is decided by
            your pair's genders when you pick a division.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { type: TeamType.SINGLES, label: 'Singles', desc: 'Just you' },
              { type: TeamType.DOUBLES, label: 'Doubles', desc: 'You + a partner' },
            ].map(({ type, label, desc }) => (
              <label
                key={type}
                className={`border rounded-md p-3 flex items-start gap-2 cursor-pointer transition-colors ${
                  teamType === type
                    ? 'border-2 border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  checked={teamType === type}
                  onChange={() => setTeamType(type)}
                  className="mt-0.5 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-gray-400">{desc}</div>
                </div>
              </label>
            ))}
          </div>
        </section>
      )}

      {/* ── Step: Partner (pairs only) ──────────────────────────────────────── */}
      {currentStep === 'partner' && (
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">{STEP_LABELS.partner}</h2>
          <p className="mt-1 text-xs text-gray-400">
            Required for doubles. Your partner must accept the invite before the team is active.
          </p>
          <div className="mt-3">
            <PartnerSearchField
              format={teamType}
              value={partner}
              onChange={p => {
                setPartner(p);
                if (p) setPartnerError('');
              }}
              disabled={teamLoading}
            />
            {partnerError && (
              <p className="mt-1 text-sm text-danger-600" role="alert">
                {partnerError}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── Step: Captain (pairs only) ──────────────────────────────────────── */}
      {currentStep === 'captain' && (
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">{STEP_LABELS.captain}</h2>
          <p className="mt-1 text-xs text-gray-400">
            The captain manages the team — enrolling it, proposing games, and submitting results.
            Creator is preselected; you can hand over captaincy after creation too.
          </p>
          <div className="mt-3 space-y-2">
            <label
              className={`border rounded-md p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                captainChoice === 'me'
                  ? 'border-2 border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300'
              }`}
            >
              <input
                type="radio"
                name="captain"
                checked={captainChoice === 'me'}
                onChange={() => setCaptainChoice('me')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="h-7 w-7 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-medium">
                {userInitials}
              </span>
              <div className="text-sm font-medium">
                {user?.firstName} {user?.lastName}{' '}
                <span className="ml-1 text-xs text-gray-400">(you)</span>
              </div>
            </label>

            {partner && (
              <label
                className={`border rounded-md p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                  captainChoice === 'partner'
                    ? 'border-2 border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <input
                  type="radio"
                  name="captain"
                  checked={captainChoice === 'partner'}
                  onChange={() => setCaptainChoice('partner')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="h-7 w-7 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-medium">
                  {partnerInitials}
                </span>
                <div className="text-sm font-medium">
                  {partner.firstName} {partner.lastName}{' '}
                  <span className="ml-1 text-xs text-gray-400">(invited partner)</span>
                </div>
              </label>
            )}
          </div>
        </section>
      )}

      {/* ── Step: Name ──────────────────────────────────────────────────────── */}
      {currentStep === 'name' && (
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">
            {STEP_LABELS.name} <span className="text-gray-400 font-normal">(optional)</span>
          </h2>
          <input
            type="text"
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
            placeholder={
              partner
                ? `${user?.lastName ?? 'You'} / ${partner.lastName}`
                : user?.firstName ?? 'My Team'
            }
            className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="mt-1 text-xs text-gray-400">Defaults to the members' names.</p>
        </section>
      )}

      {/* ── Step: Divisions (mandatory, multi-select) ───────────────────────── */}
      {currentStep === 'divisions' && (
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">
            {STEP_LABELS.divisions} <span className="text-danger-600">*</span>
          </h2>
          <p className="mt-1 text-xs text-gray-400">
            Choose at least one division ladder to play in. An admin places your team into a tier
            after enrollment.
          </p>
          {!leagueId ? (
            <p className="mt-2 text-sm text-gray-500">
              No league is selected. Pick a league from the banner first.
            </p>
          ) : seasonHasNoDivisions ? (
            <p className="mt-2 text-sm text-gray-500">
              No divisions available for this league's current season yet.
            </p>
          ) : (
            <EnrollDivisionPicker
              divisions={divisions}
              genders={genders}
              selected={selectedGroupIds}
              onChange={setSelectedGroupIds}
              loading={leagueLoading && divisions.length === 0}
            />
          )}
        </section>
      )}

      {/* Error */}
      {error && (
        <div className="mt-3 rounded-md bg-danger-50 p-3" role="alert">
          <p className="text-sm text-danger-800">{error}</p>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="flex gap-3">
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={goBack}
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              Back
            </button>
          )}
          <Link
            to="/teams"
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Link>
        </div>

        {currentStep !== 'divisions' ? (
          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canSubmit}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating…' : 'Create & enroll team'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateTeamPage;
